import { APIError, HTTPError, ValidationMessage } from '@tlx/astro-shared';
import { isEmpty, stripWhiteSpace } from '@Util/stringUtils';
import { Dispatch, SetStateAction } from 'react';

export function normalizeError(error: unknown) {
    return normalizeApiError(error) ?? normalizeHttpError(error);
}

export function normalizeApiError(error: unknown) {
    if (isAPIError(error)) {
        return getApiErrorMessage(error);
    }

    return undefined;
}

export function normalizeHttpError(error: unknown) {
    if (isHttpError(error)) {
        return getHttpErrorMessage(error);
    }

    return undefined;
}

function getApiErrorMessage(error: APIError): string {
    const message: string[] = [];
    message.push(error.details.message);
    message.push(getValidateMessage(error));
    message.push('Status: ' + error.details.status);

    return message
        .filter((m) => !isEmpty(m))
        .map((m) => removeSeparator(m))
        .join(', ');
}

function removeSeparator(text: string) {
    return text.slice(-1) === '.' ? text.substring(0, text.length - 1) : text;
}

function getValidateMessage(apiError: APIError) {
    if (isValidationMessages(apiError.details.validationMessages)) {
        const message = apiError.details.validationMessages.filter((e) => isValidationMessage(e)).map((e) => e.message);

        return message.join(', ');
    }

    return '';
}

export function isAPIError(error: unknown): error is APIError {
    return error instanceof APIError;
}

function isValidationMessage(message: unknown): message is ValidationMessage {
    return message !== undefined && message !== null && typeof message === 'object' && 'message' in message;
}

function isValidationMessages(validationMessages: unknown): validationMessages is ValidationMessage[] {
    return (
        validationMessages != undefined &&
        Array.isArray(validationMessages) &&
        validationMessages.length > 0 &&
        validationMessages.some((e) => isValidationMessage(e))
    );
}

export function isHttpError(response: unknown): response is HTTPError {
    return (
        response !== undefined &&
        response !== null &&
        typeof response === 'object' &&
        'message' in response &&
        'name' in response
    );
}

function getHttpErrorMessage(error: HTTPError): string {
    return error.message;
}

export function isValidationError(error: APIError) {
    return error.details.code === 18000;
}

// I would refactor this in two methods that just give a new set one for employee ids
// and the other for non-employee ids. And then you can just remove the setters and
// the closing flag can be moved outside based on the value of the non-employee ids set.
export function mapValidationMessages(
    validationMessages: ValidationMessage[] | null,
    setValidationsMap: Dispatch<SetStateAction<Map<number, string[]>>>,
    setValidationsWithoutField: Dispatch<SetStateAction<string[]>>,
    defaultValidationMessage: string,
    onClose: () => void,
) {
    let closeFlag = true;
    if (validationMessages !== null) {
        //validation with correct field meaning it should be displayed in table
        const validationMessagesToShowInTable = validationMessages.filter(
            (validationMessage) => validationMessage.field != null && /^[0-9, ]*$/.test(validationMessage.field),
        );
        if (validationMessagesToShowInTable.length > 0) {
            const newMap = new Map();
            for (const validationMessage of validationMessagesToShowInTable) {
                const employeeIds =
                    stripWhiteSpace(validationMessage.field ?? '')
                        .split(',')
                        .map(Number) ?? [];
                for (const employeeId of employeeIds) {
                    if (newMap.has(employeeId)) {
                        newMap.set(employeeId, newMap.get(employeeId).concat(validationMessage.message));
                    } else {
                        newMap.set(employeeId, [validationMessage.message]);
                    }
                }
            }
            setValidationsMap(newMap);
        }
        //validation with no or wrong format on field meaning a message that should be displayed in modal
        const validationMessagesToShowInModal = validationMessages.filter(
            (validationMessage) => !validationMessagesToShowInTable.includes(validationMessage),
        );
        if (validationMessagesToShowInModal.length > 0) {
            closeFlag = false;
            setValidationsWithoutField(
                validationMessagesToShowInModal.map((vm) => vm.message).filter((vm, i, a) => a.indexOf(vm) === i),
            );
        }
    } else {
        closeFlag = false;
        setValidationsWithoutField([defaultValidationMessage]);
    }
    //close modal if only table validations
    if (closeFlag) {
        onClose();
    }
}
